import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../../components/DashboardLayout";
import api, { downloadPdf } from "../../lib/api";
import { Button } from "../../components/ui/button";
import CountUp from "../../components/CountUp";
import {
  Award,
  ScrollText,
  Globe2,
  Sparkles,
  ArrowUpRight,
  Trophy,
  Crown,
  BookOpen,
  Mic,
  Compass,
  Zap,
  Handshake,
  Users,
  Calendar,
  Loader2,
  Brain,
  Download,
  GraduationCap,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  Handshake, Compass, BookOpen, Mic, Crown, Zap, ScrollText, Globe2, GraduationCap,
};

export default function PassportPage() {
  const { delegateId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scorecardLoading, setScorecardLoading] = useState(false);
  const [recentScorecard, setRecentScorecard] = useState(null);

  useEffect(() => {
    const path = delegateId ? `/passport/${delegateId}` : "/passport/me";
    api.get(path)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load passport"))
      .finally(() => setLoading(false));

    const scorecardPath = delegateId
      ? `/passport/scorecard/${delegateId}`
      : "/passport/scorecard/me";
    api.get(scorecardPath)
      .then((r) => setRecentScorecard(r.data?.[0] || null))
      .catch(() => {});
  }, [delegateId]);

  const generateScorecard = async () => {
    setScorecardLoading(true);
    try {
      const targetId = delegateId || data?.delegate?.id;
      const { data: card } = await api.post("/passport/scorecard/generate", {
        delegate_id: targetId,
      });
      setRecentScorecard(card);
      toast.success("AI Leadership Scorecard generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate scorecard");
    } finally {
      setScorecardLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto py-20 text-center">
          <Loader2 className="h-6 w-6 text-[#0F4C81] animate-spin mx-auto" />
          <p className="mt-3 text-sm text-slate-500">Loading passport…</p>
        </div>
      </DashboardLayout>
    );
  }
  if (!data) return null;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto pb-16">
        {/* Hero passport card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-sm bg-brand-navy-dark text-brand-champagne p-8 lg:p-10 border border-brand-gold/30 shadow-editorial"
          data-testid="passport-hero"
        >
          {/* Foil-stripe top */}
          <div aria-hidden className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
          {/* Parchment noise + halos */}
          <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
          <div aria-hidden className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-gold/12 blur-3xl" />
          <div aria-hidden className="absolute -left-10 -bottom-10 h-60 w-60 rounded-full bg-brand-midblue/30 blur-3xl" />

          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="eyebrow text-brand-gold flex items-center gap-2">
                <Sparkles className="h-3 w-3" /> Global Delegate Passport
              </div>
              <h1 className="mt-4 font-display text-3xl sm:text-5xl text-brand-champagne leading-tight">
                {data.delegate.name}
              </h1>
              <div className="divider-gold-hairline mt-5 max-w-xs" />
              <p className="mt-5 text-brand-champagne/65 text-sm">
                {data.delegate.school || "Independent delegate"} · {data.delegate.experience_level || "—"}
              </p>

              <div className="mt-8 flex items-center gap-4 flex-wrap">
                <motion.span
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 250, damping: 18 }}
                  className="relative px-4 py-2 rounded-sm font-bold text-xs uppercase tracking-[0.22em] shadow-gold-soft"
                  style={{ backgroundColor: data.level.color, color: "#0A192F" }}
                  data-testid="passport-level"
                >
                  <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-brand-gold animate-ping" />
                  <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-brand-gold" />
                  {data.level.label}
                </motion.span>
                <motion.span
                  key={data.points}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="font-display text-4xl text-brand-gold drop-shadow-[0_0_20px_rgba(212,175,55,0.35)]"
                  data-testid="passport-points"
                >
                  <CountUp
                    to={data.points}
                    duration={1.6}
                    milestoneEvery={50}
                    milestoneMin={50}
                    testid="passport-points-countup"
                  />
                  <span className="ml-1 text-sm font-sans tracking-[0.22em] text-brand-gold/70 uppercase">PTS</span>
                </motion.span>
              </div>

              {data.next_level && (
                <div className="mt-6 max-w-md">
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                    <span className="inline-flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3 text-[#D4AF37]" />
                      Progress to {data.next_level.label}
                    </span>
                    <span className="font-mono font-bold text-[#D4AF37]">{data.progress_to_next}%</span>
                  </div>
                  <div className="relative h-2.5 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] via-[#f6d272] to-[#D4AF37] bg-[length:200%_100%]"
                      initial={{ width: 0 }}
                      animate={{
                        width: `${data.progress_to_next}%`,
                        backgroundPosition: ["0% 0%", "100% 0%"],
                      }}
                      transition={{
                        width: { duration: 1.2, ease: "easeOut" },
                        backgroundPosition: { duration: 3, repeat: Infinity, ease: "linear" },
                      }}
                      data-testid="passport-progress"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-5 grid grid-cols-3 gap-3">
              <Stat label="Events" value={data.events_count} icon={Calendar} />
              <Stat label="Countries" value={data.countries_represented.length} icon={Globe2} />
              <Stat label="Certificates" value={data.certificate_count} icon={ScrollText} />
              <Stat label="Badges" value={data.badges.length} icon={Award} gold />
              <Stat label="Skills ≥ 80" value={Object.values(data.skills).filter(v => v >= 80).length} icon={Sparkles} />
              <Stat label="Councils" value={data.councils_participated.length} icon={Users} />
            </div>
          </div>
        </motion.div>

        {/* Skills + Badges row */}
        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          {/* Skills */}
          <div className="lift-card lg:col-span-7 rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  Skill Intelligence
                </p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight text-[#0A192F]">
                  10-skill leadership profile
                </h2>
              </div>
              <Link
                to="/dashboard/leaderboard"
                className="text-xs font-semibold text-[#0F4C81] hover:text-[#0A192F] inline-flex items-center gap-1"
              >
                Leaderboard <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4" data-testid="passport-skills">
              {data.skills_meta.map((s, idx) => {
                const v = data.skills[s.key] || 0;
                const tier = _skillTier(v);
                const barColor = v >= 80
                  ? "from-[#D4AF37] via-[#f6d272] to-[#D4AF37]"
                  : v >= 60
                  ? "from-[#0F4C81] via-[#3B82F6] to-[#0F4C81]"
                  : "from-slate-400 to-slate-500";
                return (
                  <motion.div
                    key={s.key}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="group"
                  >
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-[#0A192F]">{s.label}</span>
                      <span className="inline-flex items-center gap-2">
                        <span className={`text-[9px] tracking-widest font-bold px-1.5 py-0.5 rounded-md ${tier.cls}`}>
                          {tier.label}
                        </span>
                        <span className="font-mono text-slate-700 tabular-nums w-6 text-right" data-testid={`skill-${s.key}`}>
                          <CountUp to={v} duration={1.0 + idx * 0.05} sparkle={false} />
                        </span>
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <motion.div
                        className={`h-full rounded-full bg-gradient-to-r ${barColor} bg-[length:200%_100%]`}
                        initial={{ width: 0 }}
                        animate={{
                          width: `${v}%`,
                          backgroundPosition: v >= 80 ? ["0% 0%", "100% 0%"] : "0% 0%",
                        }}
                        transition={{
                          width: { duration: 1, delay: idx * 0.04, ease: "easeOut" },
                          backgroundPosition: { duration: 3, repeat: Infinity, ease: "linear" },
                        }}
                      />
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">{s.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Badges */}
          <div className="lift-card lg:col-span-5 rounded-2xl bg-white border border-slate-200 p-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                Earned badges
              </p>
              <h2 className="mt-1 text-xl font-semibold tracking-tight text-[#0A192F]">
                {data.badges.length} of {data.badges_meta.length}
              </h2>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3" data-testid="passport-badges">
              {data.badges_meta.map((b, idx) => {
                const earned = data.badges.find((x) => x.badge_key === b.key);
                const Icon = ICONS[b.icon] || Trophy;
                return (
                  <motion.div
                    key={b.key}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    whileHover={earned ? { y: -2, scale: 1.03 } : {}}
                    className={`group relative rounded-lg p-3 border overflow-hidden ${
                      earned
                        ? "bg-gradient-to-br from-[#D4AF37]/20 via-[#D4AF37]/5 to-transparent border-[#D4AF37]/50 shadow-[0_0_24px_rgba(212,175,55,0.15)]"
                        : "bg-slate-50 border-slate-200 opacity-60"
                    }`}
                    data-testid={`badge-${b.key}${earned ? "-earned" : ""}`}
                  >
                    {/* Shine sweep on hover for earned badges */}
                    {earned && (
                      <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent group-hover:translate-x-full transition-transform duration-700 ease-out" />
                    )}
                    <div className={`relative h-10 w-10 rounded-md flex items-center justify-center ${
                      earned
                        ? "bg-gradient-to-br from-[#f6d272] to-[#D4AF37] text-[#0A192F] shadow-md"
                        : "bg-slate-200 text-slate-400"
                    }`}>
                      <Icon className="h-5 w-5" />
                      {earned && (
                        <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-white" />
                      )}
                    </div>
                    <p className={`relative mt-2.5 text-xs font-bold leading-tight ${earned ? "text-[#0A192F]" : "text-slate-500"}`}>
                      {b.label}
                    </p>
                    <p className="relative mt-1 text-[10px] text-slate-500 leading-tight">{b.blurb}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Events attended */}
        <div className="lift-card mt-8 rounded-2xl bg-white border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                Conference history
              </p>
              <h2 className="mt-1 text-xl font-semibold tracking-tight text-[#0A192F]">
                Where I've represented the world
              </h2>
            </div>
            <span className="text-xs text-slate-500">{data.events_attended.length} events</span>
          </div>
          <div className="mt-5 divide-y divide-slate-100" data-testid="passport-events">
            {data.events_attended.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-500">No conferences yet — your story begins with the first delegate badge.</p>
            ) : data.events_attended.map((ev) => (
              <div key={ev.id} className="py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#0A192F]">{ev.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {new Date(ev.start_date).toLocaleDateString()} · {ev.format} · {ev.location}
                  </p>
                </div>
                {ev.country && (
                  <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-[10px] uppercase tracking-[0.18em] font-bold">
                    Repping: {ev.country}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* AI leadership scorecard */}
        <div className="mt-8 rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-white p-8 border border-[#D4AF37]/20">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[#D4AF37] text-[#0A192F] flex items-center justify-center">
              <Brain className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                AI Leadership Scorecard
              </p>
              <h2 className="text-xl font-semibold tracking-tight">
                Your post-conference leadership report
              </h2>
            </div>
          </div>

          {recentScorecard ? (
            <ScorecardView card={recentScorecard} />
          ) : (
            <div className="mt-6">
              <p className="text-sm text-slate-300 max-w-2xl">
                Generate an AI-powered leadership scorecard based on your skills, papers, quizzes
                and chair feedback. Get personalised next steps for your next conference.
              </p>
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Button
              onClick={generateScorecard}
              disabled={scorecardLoading}
              className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
              data-testid="generate-scorecard-button"
            >
              {scorecardLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analysing your journey...</>
              ) : recentScorecard ? "Regenerate scorecard" : "Generate scorecard"}
            </Button>
            {recentScorecard && (
              <Button
                onClick={async () => {
                  try {
                    await downloadPdf(`/scorecards/${recentScorecard.id}/pdf`);
                    toast.success("Scorecard PDF downloaded");
                  } catch {
                    toast.error("Could not download scorecard PDF.");
                  }
                }}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 font-semibold"
                data-testid="download-scorecard-button"
              >
                <Download className="h-4 w-4 mr-1.5" /> Download PDF
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value, icon: Icon, gold = false }) {
  return (
    <motion.div
      whileHover={{ y: -3, scale: 1.03 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className={`relative rounded-xl p-3 border overflow-hidden ${
        gold
          ? "bg-gradient-to-br from-[#D4AF37]/25 to-[#D4AF37]/5 border-[#D4AF37]/50 shadow-[0_0_20px_rgba(212,175,55,0.18)]"
          : "bg-white/5 border-white/10 hover:border-white/20"
      }`}
    >
      <Icon className={`h-4 w-4 ${gold ? "text-[#D4AF37]" : "text-slate-300"}`} />
      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, type: "spring" }}
        className={`mt-2 font-serif text-2xl ${gold ? "text-[#D4AF37] drop-shadow-[0_0_10px_rgba(212,175,55,0.35)]" : "text-white"}`}
      >
        <CountUp
          to={value}
          duration={1.2}
          sparkle={gold}
          milestoneEvery={5}
          milestoneMin={5}
          testid={`passport-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
        />
      </motion.p>
      <p className="text-[9px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">{label}</p>
    </motion.div>
  );
}

function _skillTier(v) {
  if (v >= 85) return { label: "PRO",    cls: "bg-[#D4AF37] text-[#0A192F]" };
  if (v >= 70) return { label: "GOLD",   cls: "bg-amber-100 text-amber-900 border border-amber-300" };
  if (v >= 55) return { label: "SILVER", cls: "bg-slate-200 text-slate-700" };
  if (v >= 40) return { label: "BRONZE", cls: "bg-orange-100 text-orange-800 border border-orange-200" };
  return { label: "ROOKIE", cls: "bg-slate-100 text-slate-500" };
}

function ScorecardView({ card }) {
  const r = card.ai_report || {};
  return (
    <div className="mt-6 grid lg:grid-cols-2 gap-6" data-testid="scorecard-view">
      <div className="lg:col-span-2">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Overall summary</p>
        <p className="mt-2 text-base text-slate-100 leading-relaxed font-serif italic" data-testid="scorecard-summary">
          "{r.overall_summary}"
        </p>
      </div>
      {r.strengths?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Strengths</p>
          <ul className="mt-2 space-y-1.5">
            {r.strengths.map((s, i) => (
              <li key={i} className="text-sm text-slate-100 flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-emerald-300 flex-shrink-0" /> {s}
              </li>
            ))}
          </ul>
        </div>
      )}
      {r.improvement_areas?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">Improvement areas</p>
          <ul className="mt-2 space-y-1.5">
            {r.improvement_areas.map((s, i) => (
              <li key={i} className="text-sm text-slate-100 flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-amber-300 flex-shrink-0" /> {s}
              </li>
            ))}
          </ul>
        </div>
      )}
      {r.recommended_courses?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Recommended courses</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {r.recommended_courses.map((c) => (
              <span key={c} className="px-2.5 py-1 rounded-full bg-white/10 text-xs text-slate-100">{c}</span>
            ))}
          </div>
        </div>
      )}
      {r.suggested_next_committee && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-bold">Suggested next committee</p>
          <p className="mt-2 font-serif text-lg text-[#D4AF37]">{r.suggested_next_committee}</p>
        </div>
      )}
      {r.motivation_quote && (
        <div className="lg:col-span-2 mt-2 pt-4 border-t border-white/10">
          <p className="text-sm text-slate-200 font-serif italic">"{r.motivation_quote}"</p>
        </div>
      )}
    </div>
  );
}
